import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import {
  AUDIENCE_WIZARD_BUTTON_CLASS,
  AUDIENCE_WIZARD_BUTTON_VARIANT,
} from '@/lib/constants/audience-wizard'

interface DiscardWorkspaceEditsDialogProps {
  open: boolean
  onKeepEditing: () => void
  onDiscard: () => void
}

/**
 * On-brand confirmation dialog shown when the user tries to leave the active
 * identity field workspace with unsaved changes. Replaces the native
 * window.confirm() call with an accessible, styled Radix AlertDialog.
 *
 * "Keep editing" → returns false (blocks navigation).
 * "Discard changes" → returns true (allows navigation).
 */
export function DiscardWorkspaceEditsDialog({
  open,
  onKeepEditing,
  onDiscard,
}: DiscardWorkspaceEditsDialogProps) {
  const { t } = useTranslation('app')
  
  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="w-[540px] gap-0 rounded-[16px] border-border dark:border-[hsl(var(--card-inset))] bg-background dark:bg-card p-0 shadow-[0px_4px_19.6px_0px_rgba(0,0,0,0.08)]">
        <div className="flex items-center justify-end p-3">
          <button
            type="button"
            onClick={onKeepEditing}
            className="text-muted-foreground hover:text-foreground dark:text-white/70 dark:hover:text-white transition-colors"
            aria-label={t('audienceCreate.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col items-center gap-7 px-[60px] pb-10">
          <AlertDialogHeader className="items-center gap-3 space-y-0">
            <AlertDialogTitle className="font-display text-2xl font-normal leading-[1.2] tracking-[-0.48px] text-foreground dark:text-white text-center">
              {t('audienceCreate.discardUnsavedEditsTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base leading-[1.6] text-muted-foreground text-center">
              {t('audienceCreate.discardUnsavedEditsBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="flex items-center justify-center gap-4">
            <AlertDialogAction
              variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
              className={cn('h-10 rounded-[6px] px-4', AUDIENCE_WIZARD_BUTTON_CLASS)}
              onClick={(event) => {
                event.preventDefault()
                onDiscard()
              }}
            >
              {t('audienceCreate.discardChanges')}
            </AlertDialogAction>
            <AlertDialogCancel
              className="h-10 rounded-[6px] border-foreground dark:border-input-focus bg-transparent px-4 text-base font-medium text-foreground dark:text-input-focus hover:bg-transparent hover:text-foreground dark:hover:text-input-focus"
              onClick={onKeepEditing}
            >
              {t('audienceCreate.keepEditing')}
            </AlertDialogCancel>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
